// web-helper/src/components/LoadingSkeleton.jsx
import React from 'react';

/**
 * Loading Skeleton Components
 *
 * Provides consistent loading states across the application.
 * Uses centralized animation keyframes from tokens.css.
 */

export const ChatSkeleton = () => (
  <div style={{
    position: 'fixed',
    bottom: 'var(--space-4)',
    right: 'var(--space-4)',
    width: '320px',
    height: '500px',
    background: 'var(--bg-primary)',
    borderRadius: 'var(--radius-xl)',
    boxShadow: 'var(--shadow-xl)',
    padding: 'var(--space-5)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-3)',
  }}>
    {/* Header skeleton */}
    <div
      className="animate-shimmer"
      style={{
        height: '60px',
        background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
        backgroundSize: '200% 100%',
        borderRadius: 'var(--radius-md)',
      }}
      aria-hidden="true"
    />

    {/* Message skeletons */}
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="animate-shimmer"
        style={{
          height: '60px',
          width: i % 2 === 0 ? '70%' : '90%',
          alignSelf: i % 2 === 0 ? 'flex-end' : 'flex-start',
          background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
          backgroundSize: '200% 100%',
          borderRadius: 'var(--radius-md)',
          animationDelay: `${i * 0.1}s`,
        }}
        aria-hidden="true"
      />
    ))}

    {/* Input skeleton */}
    <div
      className="animate-shimmer"
      style={{
        height: '48px',
        marginTop: 'auto',
        background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
        backgroundSize: '200% 100%',
        borderRadius: 'var(--radius-md)',
      }}
      aria-hidden="true"
    />

    <span className="sr-only">Loading chat interface...</span>
  </div>
);

export const AdminSkeleton = () => (
  <div
    style={{
      minHeight: '100vh',
      background: 'var(--bg-secondary)',
      padding: 'var(--space-5)',
    }}
    role="status"
    aria-label="Loading admin dashboard"
  >
    {/* Header skeleton */}
    <div
      className="animate-shimmer"
      style={{
        height: '80px',
        background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
        backgroundSize: '200% 100%',
        borderRadius: 'var(--radius-md)',
        marginBottom: 'var(--space-5)',
      }}
      aria-hidden="true"
    />

    {/* Content grid skeleton */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: 'var(--space-4)',
    }}>
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="animate-shimmer"
          style={{
            height: '200px',
            background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
            backgroundSize: '200% 100%',
            borderRadius: 'var(--radius-md)',
            animationDelay: `${i * 0.1}s`,
          }}
          aria-hidden="true"
        />
      ))}
    </div>

    <span className="sr-only">Loading admin dashboard...</span>
  </div>
);

export const LoadingSpinner = ({ size = 'medium', label = 'Loading...' }) => {
  const sizes = {
    small: { spinner: '24px', border: '3px', minHeight: '100px' },
    medium: { spinner: '40px', border: '4px', minHeight: '200px' },
    large: { spinner: '56px', border: '5px', minHeight: '300px' },
  };

  const currentSize = sizes[size] || sizes.medium;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: currentSize.minHeight,
        gap: 'var(--space-3)',
      }}
      role="status"
      aria-label={label}
    >
      <div
        className="animate-spin"
        style={{
          width: currentSize.spinner,
          height: currentSize.spinner,
          border: `${currentSize.border} solid var(--gray-200)`,
          borderTop: `${currentSize.border} solid var(--compudopt-blue)`,
          borderRadius: '50%',
        }}
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </div>
  );
};

/**
 * TableSkeleton - Loading skeleton for data tables
 */
export const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <div
    style={{
      background: 'var(--bg-primary)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-default)',
      overflow: 'hidden',
    }}
    role="status"
    aria-label="Loading table data"
  >
    {/* Header */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 'var(--space-4)',
      padding: 'var(--space-4)',
      background: 'var(--bg-secondary)',
      borderBottom: '2px solid var(--border-default)',
    }}>
      {Array.from({ length: columns }).map((_, i) => (
        <div
          key={`header-${i}`}
          className="animate-shimmer"
          style={{
            height: '20px',
            background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
            backgroundSize: '200% 100%',
            borderRadius: 'var(--radius-sm)',
          }}
          aria-hidden="true"
        />
      ))}
    </div>

    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        key={`row-${rowIndex}`}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 'var(--space-4)',
          padding: 'var(--space-4)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div
            key={`cell-${rowIndex}-${colIndex}`}
            className="animate-shimmer"
            style={{
              height: '16px',
              width: colIndex === 0 ? '60%' : '80%',
              background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
              backgroundSize: '200% 100%',
              borderRadius: 'var(--radius-sm)',
              animationDelay: `${(rowIndex * columns + colIndex) * 0.05}s`,
            }}
            aria-hidden="true"
          />
        ))}
      </div>
    ))}

    <span className="sr-only">Loading table data...</span>
  </div>
);

/**
 * CardSkeleton - Loading skeleton for card content
 */
export const CardSkeleton = ({ height = '200px' }) => (
  <div
    className="animate-shimmer"
    style={{
      height,
      background: 'linear-gradient(90deg, var(--gray-200) 0%, var(--gray-300) 50%, var(--gray-200) 100%)',
      backgroundSize: '200% 100%',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-default)',
    }}
    role="status"
    aria-label="Loading content"
  >
    <span className="sr-only">Loading...</span>
  </div>
);
